import React, { useState, useContext } from "react";
import { Formik, Form, Field } from "formik";
import MyContext from "../MyContext/MyContext";

/* ---------- ALERT COMPONENT ---------- */
const Alert = ({ type, children }) => {
  const styles = {
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 12,
    color: type === "success" ? "#064e3b" : "#6b0219",
    background: type === "success" ? "#bbf7d0" : "#fecaca",
    border: `1px solid ${type === "success" ? "#bbf7d0" : "#fecaca"}`,
    fontSize: 14,
  };
  return <div style={styles}>{children}</div>;
};

const Account = () => {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const { loginUser, registerUser, requestReset } = useContext(MyContext);

  // password toggles
  const [showLoginPass, setShowLoginPass] = useState(false);
  const [showRegisterPass, setShowRegisterPass] = useState(false);

  return (
    <section className="account py-80">
      <div className="container container-lg">
        <div className="row justify-content-center">
          <div className="col-xl-6">
            <div className="border rounded-16 px-24 py-40">

              {/* LOGIN ------------------------------------------------------ */}
              {mode === "login" && (
                <>
                  <h4 className="mb-20">Login</h4>

                  <Formik
                    initialValues={{ email: "", password: "" }}
                    onSubmit={async (values, { setSubmitting, setStatus }) => {
                      setStatus(null);
                      try {
                        const res = await loginUser(values.email, values.password);
                        if (!res.success) setStatus({ error: res.error });
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({ isSubmitting, status }) => (
                      <Form>
                        {status?.error && <Alert type="error">{status.error}</Alert>}
                        {status?.success && <Alert type="success">{status.success}</Alert>}

                        <div className="mb-16">
                          <label>Email</label>
                          <Field type="email" name="email" className="common-input" required />
                        </div>

                        <div className="mb-12">
                          <label>Password</label>
                          <div style={{ position: "relative" }}>
                            <Field
                              type={showLoginPass ? "text" : "password"}
                              name="password"
                              className="common-input"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowLoginPass(p => !p)}
                              style={{
                                position: "absolute",
                                right: 8,
                                top: 8,
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                fontSize: 13,
                              }}
                            >
                              {showLoginPass ? "Hide" : "Show"}
                            </button>
                          </div>
                        </div>

                        <button type="submit" className="btn btn-main w-100" disabled={isSubmitting}>
                          {isSubmitting ? "Logging in..." : "Log in"}
                        </button>

                        <div className="mt-16 text-center">
                          <button
                            type="button"
                            onClick={() => setMode("forgot")}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#1976d2",
                              cursor: "pointer",
                              fontSize: 14,
                            }}
                          >
                            Forgot password?
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>

                  <div className="mt-24 text-center">
                    <span>Don't have an account? </span>
                    <button
                      onClick={() => setMode("register")}
                      className="text-main-600"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#1976d2",
                      }}
                    >
                      Register →
                    </button>
                  </div>
                </>
              )}

              {/* REGISTER ------------------------------------------------------ */}
              {mode === "register" && (
                <>
                  <h4 className="mb-20">Create account</h4>

                  <Formik
                    initialValues={{ name: "", email: "", mobile: "", password: "" }}
                    onSubmit={async (values, { setSubmitting, setStatus }) => {
                      setStatus(null);
                      try {
                        const res = await registerUser(values);
                        if (!res.success) setStatus({ error: res.error });
                        else {
                          setStatus({ success: res.message || "Registration successful!" });
                          setTimeout(() => setMode("login"), 1200);
                        }
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({ isSubmitting, status }) => (
                      <Form>
                        {status?.error && <Alert type="error">{status.error}</Alert>}
                        {status?.success && <Alert type="success">{status.success}</Alert>}

                        <div className="mb-12">
                          <label>Name</label>
                          <Field name="name" className="common-input" required />
                        </div>

                        <div className="mb-12">
                          <label>Email</label>
                          <Field type="email" name="email" className="common-input" required />
                        </div>

                        <div className="mb-12">
                          <label>Mobile</label>
                          <Field name="mobile" className="common-input" required />
                        </div>

                        <div className="mb-12">
                          <label>Password</label>
                          <div style={{ position: "relative" }}>
                            <Field
                              type={showRegisterPass ? "text" : "password"}
                              name="password"
                              className="common-input"
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowRegisterPass(p => !p)}
                              style={{
                                position: "absolute",
                                right: 8,
                                top: 8,
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                fontSize: 13,
                              }}
                            >
                              {showRegisterPass ? "Hide" : "Show"}
                            </button>
                          </div>
                        </div>

                        <button type="submit" className="btn btn-main w-100" disabled={isSubmitting}>
                          {isSubmitting ? "Registering..." : "Register"}
                        </button>
                      </Form>
                    )}
                  </Formik>

                  <div className="mt-24 text-center">
                    <span>Already have an account? </span>
                    <button
                      onClick={() => setMode("login")}
                      className="text-main-600"
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#1976d2" }}
                    >
                      Log in →
                    </button>
                  </div>
                </>
              )}

              {/* FORGOT PASSWORD ------------------------------------------------------ */}
              {mode === "forgot" && (
                <>
                  <h4 className="mb-20">Reset your password</h4>
                  <p style={{ marginBottom: 12, color: "#555" }}>
                    Enter your email and we will send you a reset link.
                  </p>

                  <Formik
                    initialValues={{ email: "" }}
                    onSubmit={async (values, { setSubmitting, setStatus }) => {
                      setStatus(null);
                      try {
                        const res = await requestReset(values.email);
                        if (!res.success) setStatus({ error: res.error });
                        else setStatus({ success: res.message });
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({ isSubmitting, status }) => (
                      <Form>
                        {status?.error && <Alert type="error">{status.error}</Alert>}
                        {status?.success && <Alert type="success">{status.success}</Alert>}

                        <div className="mb-12">
                          <label>Email</label>
                          <Field type="email" name="email" className="common-input" required />
                        </div>

                        <button type="submit" className="btn btn-main w-100" disabled={isSubmitting}>
                          {isSubmitting ? "Sending..." : "Send reset link"}
                        </button>

                        <div className="mt-24 text-center">
                          <button
                            type="button"
                            onClick={() => setMode("login")}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#1976d2",
                            }}
                          >
                            Back to login →
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Account;
